import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Modal, Popover, QRCode, Skeleton, Spin, Tooltip, message } from 'antd';
import {
  AppstoreOutlined,
  ArrowLeftOutlined,
  BarChartOutlined,
  BulbOutlined,
  CheckCircleFilled,
  CloseOutlined,
  InfoCircleOutlined,
  ShopOutlined,
  TeamOutlined,
  UserOutlined,
  WechatFilled,
} from '@ant-design/icons';
import {
  createAccountPaySession,
  getAccountPaymentEntitlement,
  listAccountPaymentPlans,
  type AccountPaySession,
  type AccountPaymentEntitlement,
  type AccountPaymentProduct,
  type AccountPaymentUpgradeDetail,
} from '../../api';
import { usePaymentOrderPolling } from '../../hooks/usePaymentOrderPolling';
import {
  ACCOUNT_PAYMENT_FEATURE_LABELS,
  ACCOUNT_PAYMENT_LIMIT_LABELS,
} from '../../../common/enums/accountPayment';
import { buildPaymentUrl } from '../../utils/accountPayment';
import { refreshAccountPaymentData } from '../../utils/accountPaymentRefresh';
import homeEmptyLeft from '../../assets/images/home-empty-left.png';
import homeEmptyRight from '../../assets/images/home-empty-right.png';
import memberScanIcon from '../../assets/images/sckan_qr.png';
import memberPayWechatIcon from '../../assets/images/member_pay_wechat.png';

const PAYMENT_QR_EXPIRE_MS = 10 * 60 * 1000;

type MembershipPaymentStep = 'overview' | 'checkout';
type MembershipFeatureGroup = TierPresentation['featureGroups'][number];

interface MembershipTierEntry {
  tierCode: string;
  plan?: AccountPaymentProduct;
  presentation: TierPresentation;
}

interface MembershipFeatureMatrixRow {
  key: string;
  title: string;
  label: string;
  values: Record<string, {
    supported: boolean;
    text: string;
  }>;
}

interface MembershipFeatureMatrixGroup {
  title: string;
  rows: MembershipFeatureMatrixRow[];
}

interface TierPresentation {
  tone: string;
  label: string;
  audience: string;
  summary: string;
  badge?: string;
  highlights: string[];
  featureGroups: Array<{
    title: string;
    items: string[];
  }>;
}

const FEATURE_GROUP_RULES: Record<string, { title: string; label: string }> = {
  DAILY_PUBLIC_LEAD: { title: '获客与私域', label: '公域线索采集' },
  DAILY_PRIVATE_LEAD: { title: '获客与私域', label: '私域线索采集' },
  LEAD_ASSISTANT_ACTIVE_COLLECT_LEAD: { title: '获客与私域', label: '主动采集线索' },
  COMMUNITY_OPERATOR: { title: '获客与私域', label: '社群运营官' },
  SELF_OPERATED_PRODUCT_DETAIL_VIEW: { title: '选品与投放', label: '自营商品详情' },
  AFFILIATE_PRODUCT_DETAIL_VIEW: { title: '选品与投放', label: '带货商品详情' },
  AFFILIATE_PRODUCT_TEST: { title: '选品与投放', label: '带货测款' },
  VIDEO_DATA_MONITOR: { title: '选品与投放', label: '发布后视频数据监控' },
  ACCOUNT_DATA_MONITOR: { title: '选品与投放', label: '账号级数据监控' },
  MULTI_DEVICE_LOGIN: { title: '账号体系', label: '多设备同时登录' },
  CREATIVE_PROMPT_ADVANCED_CATEGORY: { title: '创意编剧', label: '高级创作分类' },
};

const LIMIT_GROUP_RULES: Record<string, { title: string; suffix: string }> = {
  leadAssistantSuggestionAccountDailyLimit: { title: '获客与素材', suffix: '每日' },
  leadAssistantReplyAccountDailyLimit: { title: '获客与素材', suffix: '每日' },
  leadFollowAccountDailyLimit: { title: '获客与素材', suffix: '每日' },
  competitorAccountLimit: { title: '获客与素材', suffix: '' },
  materialVideoDnaAccountDailyLimit: { title: '获客与素材', suffix: '每日' },
  scriptPromptGenerateAccountDailyLimit: { title: '创意编剧', suffix: '每日' },
  creativeCharacterTotalLimit: { title: '创意编剧', suffix: '' },
  multiDeviceLoginLimit: { title: '账号体系', suffix: '' },
};

// 值为 0 表示「不限」的 limit key
const ZERO_MEANS_UNLIMITED_KEYS = new Set(['creativeCharacterTotalLimit']);

interface MembershipPaymentModalProps {
  open: boolean;
  onClose: () => void;
  initialPlanCode?: string;
  mode?: 'new' | 'renew';
  currentPlanExpireAt?: string | null;
  onPaymentSuccess?: () => void | Promise<void>;
}

function formatCompute(value?: number | string) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) {
    return '0';
  }
  return Math.max(0, numeric).toLocaleString('zh-CN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    useGrouping: false,
  });
}

function formatPrice(value?: string | number) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) {
    return String(value || '0');
  }
  return numeric.toLocaleString('zh-CN', {
    minimumFractionDigits: numeric % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  });
}

function formatAmountTotalYuan(value?: number) {
  return formatPrice(Number(value || 0) / 100);
}

function resolvePlanLevel(plan?: AccountPaymentProduct | AccountPaymentEntitlement | null) {
  const directLevel = Number((plan as AccountPaymentProduct | undefined)?.level || 0);
  if (Number.isFinite(directLevel) && directLevel > 0) {
    return directLevel;
  }
  const tierCode = (
    (plan as AccountPaymentProduct | undefined)?.planTierCode
    || (plan as AccountPaymentProduct | undefined)?.extraConfig?.planTierCode
    || ''
  ).toString().toUpperCase();
  if (tierCode.includes('FLAGSHIP')) return 3;
  if (tierCode.includes('PRO')) return 2;
  if (tierCode.includes('BASIC')) return 1;
  const name = ((plan as AccountPaymentEntitlement | undefined)?.planName || '').toString();
  if (name.includes('旗舰')) return 3;
  if (name.includes('专业')) return 2;
  if (name.includes('基础')) return 1;
  return 0;
}

function resolvePlanTierCode(plan?: AccountPaymentProduct | AccountPaymentEntitlement | null) {
  const rawCode = (
    (plan as AccountPaymentProduct | undefined)?.planTierCode
    || (plan as AccountPaymentProduct | undefined)?.extraConfig?.planTierCode
    || ''
  ).toString().trim().toUpperCase();
  if (rawCode.includes('FLAGSHIP')) return 'FLAGSHIP';
  if (rawCode.includes('PRO')) return 'PRO';
  if (rawCode.includes('BASIC')) return 'BASIC';
  const name = (
    (plan as AccountPaymentEntitlement | undefined)?.planName
    || (plan as AccountPaymentProduct | undefined)?.name
    || (plan as AccountPaymentProduct | undefined)?.productName
    || ''
  ).toString();
  if (name.includes('旗舰')) return 'FLAGSHIP';
  if (name.includes('专业')) return 'PRO';
  if (name.includes('基础')) return 'BASIC';
  const level = resolvePlanLevel(plan);
  if (level >= 3) return 'FLAGSHIP';
  if (level === 2) return 'PRO';
  if (level === 1) return 'BASIC';
  return 'CUSTOM';
}

function resolvePlanTierLabel(plan?: AccountPaymentProduct | null) {
  const configuredName = plan?.planTierName?.trim();
  if (configuredName) {
    return configuredName;
  }

  const name = plan?.name || plan?.productName || '';
  return name;
}

function resolvePlanDurationLabel(plan: AccountPaymentProduct) {
  const months = Number(plan.billingMonths || plan.extraConfig?.billingMonths || 0);
  if (Number.isFinite(months) && months > 0) {
    if (months === 1) return '月付';
    if (months === 3) return '季付';
    if (months === 12) return '年付';
    return `${months}个月`;
  }

  if (plan.durationDays) {
    if (plan.durationDays >= 360) return '年付';
    if (plan.durationDays >= 85 && plan.durationDays <= 95) return '季付';
    if (plan.durationDays >= 28 && plan.durationDays <= 31) return '月付';
    return `${plan.durationDays}天`;
  }
  return '套餐';
}

function resolvePlanDurationSort(plan: AccountPaymentProduct) {
  const months = Number(plan.billingMonths || plan.extraConfig?.billingMonths || 0);
  if (Number.isFinite(months) && months > 0) {
    return months;
  }
  return Number(plan.durationDays || 0) / 31;
}

function resolvePlanDurationTitle(plan: AccountPaymentProduct) {
  const months = Number(plan.billingMonths || plan.extraConfig?.billingMonths || 0);
  if (Number.isFinite(months) && months > 0) {
    if (months === 1) return '1个月';
    if (months === 3) return '3个月';
    if (months === 12) return '12个月';
    return `${months}个月`;
  }
  return plan.durationDays ? `${plan.durationDays}天` : resolvePlanDurationLabel(plan);
}

function resolvePlanMonthlyAmount(plan: AccountPaymentProduct) {
  const amount = Number(plan.amountTotal || 0);
  const months = Math.max(1, resolvePlanDurationSort(plan));
  return amount / months;
}

function resolveShopLimitText(plan: AccountPaymentProduct) {
  const value = plan.shopLimit;
  return typeof value === 'number' && value > 0 ? `${value}个店铺席位` : '';
}

function resolvePlanTotalCompute(plan?: AccountPaymentProduct | null) {
  return Number(plan?.totalComputePowerAmount ?? plan?.computePowerAmount ?? 0);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function normalizeBenefitItem(item: unknown) {
  if (typeof item === 'string') {
    return item.trim();
  }
  if (!isRecord(item)) {
    return '';
  }

  const label = item.name ?? item.label ?? item.text ?? item.title ?? item.value;
  return typeof label === 'string' ? label.trim() : '';
}

function normalizeTextArray(value: unknown) {
  return readBenefitArray(value);
}

function normalizeFeatureGroups(value: unknown): TierPresentation['featureGroups'] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => {
      if (!isRecord(item)) {
        return null;
      }
      const title = typeof item.title === 'string' ? item.title.trim() : '';
      const items = normalizeTextArray(item.items);
      return title && items.length ? { title, items } : null;
    })
    .filter((item): item is TierPresentation['featureGroups'][number] => Boolean(item));
}

function addFeatureGroupItem(groups: Map<string, string[]>, title: string, item: string) {
  const normalizedTitle = title.trim();
  const normalizedItem = item.trim();
  if (!normalizedTitle || !normalizedItem) {
    return;
  }
  const items = groups.get(normalizedTitle) || [];
  if (!items.includes(normalizedItem)) {
    items.push(normalizedItem);
  }
  groups.set(normalizedTitle, items);
}

function addFeatureGroups(groups: Map<string, string[]>, featureGroups: MembershipFeatureGroup[]) {
  featureGroups.forEach((group) => {
    group.items.forEach((item) => addFeatureGroupItem(groups, group.title, item));
  });
}

function formatLimitFeature(label: string, value: unknown, suffix: string, key?: string) {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  const numeric = Number(value);
  // limit=0 表示不限制
  if (key && ZERO_MEANS_UNLIMITED_KEYS.has(key) && numeric === 0) {
    return `${label} 不限`;
  }
  const displayValue = Number.isFinite(numeric) ? formatCompute(numeric) : String(value);
  if (!displayValue) {
    return '';
  }
  if (suffix === '每日') {
    return `${label} ${displayValue} 次/日`;
  }
  if (label.includes('账号') || label.includes('席位') || label.includes('设备')) {
    return `${label} ${displayValue} 个`;
  }
  return `${label} ${displayValue} 个`;
}

function buildPlanFeatureGroups(plan?: AccountPaymentProduct | null, configuredGroups: MembershipFeatureGroup[] = []) {
  const groups = new Map<string, string[]>();
  addFeatureGroups(groups, configuredGroups);

  if (!plan) {
    return Array.from(groups.entries()).map(([title, items]) => ({ title, items }));
  }

  const monthlyCompute = Number(plan.monthlyComputePowerAmount ?? plan.computePowerAmount ?? 0);
  const shopLimitText = resolveShopLimitText(plan);
  if (shopLimitText) {
    addFeatureGroupItem(groups, '账号体系', shopLimitText);
  }
  if (Number.isFinite(monthlyCompute) && monthlyCompute > 0) {
    addFeatureGroupItem(groups, '账号体系', `${formatCompute(monthlyCompute)} 元/月赠送算力`);
  }

  const featureTags = plan.featureTags || [];
  featureTags.forEach((tag) => {
    const rule = FEATURE_GROUP_RULES[tag];
    if (rule) {
      addFeatureGroupItem(groups, rule.title, rule.label);
      return;
    }
    addFeatureGroupItem(groups, '其他权益', ACCOUNT_PAYMENT_FEATURE_LABELS[tag] || tag);
  });

  Object.entries(plan.limits || {}).forEach(([key, value]) => {
    const label = ACCOUNT_PAYMENT_LIMIT_LABELS[key];
    if (!label) {
      return;
    }
    const rule = LIMIT_GROUP_RULES[key] || { title: '数量限制', suffix: '' };
    const text = formatLimitFeature(label, value, rule.suffix, key);
    if (text) {
      addFeatureGroupItem(groups, rule.title, text);
    }
  });

  if (!featureTags.includes('MULTI_DEVICE_LOGIN') && !plan.limits?.multiDeviceLoginLimit) {
    addFeatureGroupItem(groups, '账号体系', '单设备登录');
  }

  return Array.from(groups.entries()).map(([title, items]) => ({ title, items }));
}

function formatFeatureMatrixLimitValue(key: string, value: unknown) {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  const numeric = Number(value);
  if (ZERO_MEANS_UNLIMITED_KEYS.has(key) && numeric === 0) {
    return '不限';
  }
  const rule = LIMIT_GROUP_RULES[key] || { title: '数量限制', suffix: '' };
  const displayValue = Number.isFinite(numeric) ? formatCompute(numeric) : String(value);
  if (!displayValue) {
    return '';
  }
  if (rule.suffix === '每日') {
    return `${displayValue} 次/日`;
  }
  return `${displayValue} 个`;
}

function pushFeatureMatrixRow(rows: MembershipFeatureMatrixRow[], row: MembershipFeatureMatrixRow, tierCodes: string[]) {
  const texts = tierCodes.map((tierCode) => row.values[tierCode]?.text || '');
  const hasDiff = new Set(texts).size > 1;
  if (hasDiff) {
    rows.push(row);
  }
}

function buildFeatureMatrixGroups(entries: MembershipTierEntry[]): MembershipFeatureMatrixGroup[] {
  const rows: MembershipFeatureMatrixRow[] = [];
  const tierCodes = entries.map((entry) => entry.tierCode);
  const tagRows = new Map<string, { title: string; label: string }>();
  const limitRows = new Map<string, { title: string; label: string }>();

  entries.forEach((entry) => {
    (entry.plan?.featureTags || []).forEach((tag) => {
      const rule = FEATURE_GROUP_RULES[tag];
      tagRows.set(`tag:${tag}`, {
        title: rule?.title || '其他权益',
        label: rule?.label || ACCOUNT_PAYMENT_FEATURE_LABELS[tag] || tag,
      });
    });
    Object.keys(entry.plan?.limits || {}).forEach((key) => {
      if (key === 'multiDeviceLoginLimit') {
        return;
      }
      const label = ACCOUNT_PAYMENT_LIMIT_LABELS[key];
      if (!label) {
        return;
      }
      const rule = LIMIT_GROUP_RULES[key];
      limitRows.set(`limit:${key}`, {
        title: rule?.title || '数量限制',
        label,
      });
    });
  });

  pushFeatureMatrixRow(rows, {
    key: 'builtin:shopLimit',
    title: '账号体系',
    label: '店铺席位',
    values: Object.fromEntries(entries.map((entry) => {
      const value = Number(entry.plan?.shopLimit || 0);
      const text = Number.isFinite(value) && value > 0 ? `${formatCompute(value)} 个` : '';
      return [entry.tierCode, { supported: Boolean(text), text: text || '未配置' }];
    })),
  }, tierCodes);

  pushFeatureMatrixRow(rows, {
    key: 'builtin:monthlyCompute',
    title: '算力豆',
    label: '月赠送算力',
    values: Object.fromEntries(entries.map((entry) => {
      const value = Number(entry.plan?.monthlyComputePowerAmount ?? entry.plan?.computePowerAmount ?? 0);
      const supported = Number.isFinite(value) && value > 0;
      return [entry.tierCode, { supported, text: supported ? `${formatCompute(value)} 元/月` : '未配置' }];
    })),
  }, tierCodes);

  pushFeatureMatrixRow(rows, {
    key: 'builtin:multiDeviceLoginLimit',
    title: '账号体系',
    label: '同时登录设备',
    values: Object.fromEntries(entries.map((entry) => {
      const rawValue = entry.plan?.limits?.multiDeviceLoginLimit;
      const configuredValue = Number(rawValue || 0);
      if (Number.isFinite(configuredValue) && configuredValue > 0) {
        return [entry.tierCode, { supported: true, text: `${formatCompute(configuredValue)} 个` }];
      }
      if (entry.plan?.featureTags?.includes('MULTI_DEVICE_LOGIN')) {
        return [entry.tierCode, { supported: true, text: '不限' }];
      }
      return [entry.tierCode, { supported: true, text: '1 个' }];
    })),
  }, tierCodes);

  tagRows.forEach((definition, key) => {
    const tag = key.replace(/^tag:/, '');
    pushFeatureMatrixRow(rows, {
      key,
      title: definition.title,
      label: definition.label,
      values: Object.fromEntries(entries.map((entry) => {
        const supported = Boolean(entry.plan?.featureTags?.includes(tag));
        return [entry.tierCode, { supported, text: supported ? '支持' : '不支持' }];
      })),
    }, tierCodes);
  });

  limitRows.forEach((definition, key) => {
    const limitKey = key.replace(/^limit:/, '');
    const rule = LIMIT_GROUP_RULES[limitKey] || { title: '数量限制', suffix: '' };
    pushFeatureMatrixRow(rows, {
      key,
      title: definition.title,
      label: definition.label,
      values: Object.fromEntries(entries.map((entry) => {
        const text = formatFeatureMatrixLimitValue(limitKey, entry.plan?.limits?.[limitKey]);
        const unlimitedText = rule.suffix === '每日' ? '不限次' : '不限';
        return [entry.tierCode, { supported: true, text: text || unlimitedText }];
      })),
    }, tierCodes);
  });

  const grouped = new Map<string, MembershipFeatureMatrixRow[]>();
  rows.forEach((row) => {
    const nextRows = grouped.get(row.title) || [];
    nextRows.push(row);
    grouped.set(row.title, nextRows);
  });
  return Array.from(grouped.entries()).map(([title, groupRows]) => ({ title, rows: groupRows }));
}

function resolveTierPresentation(plan?: AccountPaymentProduct | null): TierPresentation {
  const rawConfig = plan?.extraConfig?.membershipUi || plan?.extraConfig?.paymentUi || plan?.extraConfig?.ui;
  if (!isRecord(rawConfig)) {
    return {
      tone: resolvePlanTierCode(plan).toLowerCase(),
      label: resolvePlanTierLabel(plan),
      audience: '',
      summary: '',
      badge: '',
      highlights: [],
      featureGroups: buildPlanFeatureGroups(plan),
    };
  }
  const highlights = normalizeTextArray(rawConfig.highlights);
  const featureGroups = normalizeFeatureGroups(rawConfig.featureGroups);
  return {
    tone: typeof rawConfig.tone === 'string' ? rawConfig.tone.trim() : '',
    label: typeof rawConfig.label === 'string' ? rawConfig.label.trim() : resolvePlanTierLabel(plan),
    audience: typeof rawConfig.audience === 'string' ? rawConfig.audience.trim() : '',
    summary: typeof rawConfig.summary === 'string' ? rawConfig.summary.trim() : '',
    badge: typeof rawConfig.badge === 'string' ? rawConfig.badge.trim() : '',
    highlights,
    featureGroups: buildPlanFeatureGroups(plan, featureGroups),
  };
}

function readBenefitArray(value: unknown) {
  if (Array.isArray(value)) {
    return value.map(normalizeBenefitItem).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(/[\n,，]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function formatMembershipExpireDate(value?: string | null) {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return `${date.getFullYear()}.${date.getMonth() + 1}.${date.getDate()}`;
}

function formatPaymentQrExpireMinutes() {
  return Math.max(1, Math.ceil(PAYMENT_QR_EXPIRE_MS / 60 / 1000));
}

function logMembershipPaymentDiagnostic(event: string, payload?: Record<string, unknown>) {
  console.info(`[MembershipPayment] ${event}`, payload || {});
}

function maskPaymentToken(value?: string | null) {
  const token = value || '';
  if (token.length <= 12) {
    return token ? `${token.slice(0, 3)}***` : '';
  }
  return `${token.slice(0, 6)}...${token.slice(-4)}`;
}

function resolvePaymentUrlDebugInfo(value: string) {
  if (!value) {
    return { host: '', hasPaySession: false, paySessionToken: '' };
  }

  try {
    const parsedUrl = new URL(value);
    return {
      host: parsedUrl.host,
      hasPaySession: parsedUrl.searchParams.has('pay_session'),
      paySessionToken: maskPaymentToken(parsedUrl.searchParams.get('pay_session')),
    };
  } catch {
    return { host: '', hasPaySession: value.includes('pay_session='), paySessionToken: '' };
  }
}

function readUpgradeDetail(paySession?: AccountPaySession | null): AccountPaymentUpgradeDetail | null {
  const detail = paySession?.plan?.upgradeDetail;
  return detail && typeof detail === 'object' ? detail : null;
}

function groupPlansByTier(plans: AccountPaymentProduct[]) {
  const grouped = new Map<string, AccountPaymentProduct[]>();
  plans.forEach((plan) => {
    const tierCode = resolvePlanTierCode(plan);
    const next = grouped.get(tierCode) || [];
    next.push(plan);
    grouped.set(tierCode, next);
  });
  return Array.from(grouped.entries())
    .map(([tierCode, items]) => ({
      tierCode,
      plans: [...items].sort((left, right) => resolvePlanDurationSort(left) - resolvePlanDurationSort(right)),
    }))
    .sort((left, right) => {
      const leftLevel = resolvePlanLevel(left.plans[0]);
      const rightLevel = resolvePlanLevel(right.plans[0]);
      return leftLevel - rightLevel;
    });
}

function resolveTierLowestMonthlyPlan(plans: AccountPaymentProduct[]) {
  return [...plans].sort((left, right) => {
    const leftMonths = resolvePlanDurationSort(left);
    const rightMonths = resolvePlanDurationSort(right);
    if (rightMonths !== leftMonths) {
      return rightMonths - leftMonths;
    }
    return resolvePlanMonthlyAmount(left) - resolvePlanMonthlyAmount(right);
  })[0] || null;
}

function resolveDefaultCheckoutPlan(plans: AccountPaymentProduct[]) {
  return [...plans].sort((left, right) => {
    const rightMonths = resolvePlanDurationSort(right);
    const leftMonths = resolvePlanDurationSort(left);
    if (rightMonths !== leftMonths) {
      return rightMonths - leftMonths;
    }
    return resolvePlanMonthlyAmount(left) - resolvePlanMonthlyAmount(right);
  })[0] || null;
}

function resolvePurchaseTypeLabel(paySession?: AccountPaySession | null) {
  if (!paySession) {
    return '';
  }
  if (paySession.purchaseType === 'UPGRADE') {
    return '升级';
  }
  if (paySession.purchaseType === 'RENEW') {
    return '续费';
  }
  return '';
}

function renderMembershipFeatureGroupIcon(title: string) {
  if (title.includes('获客') || title.includes('线索') || title.includes('素材')) {
    return <UserOutlined />;
  }
  if (title.includes('选品') || title.includes('投放') || title.includes('商品')) {
    return <ShopOutlined />;
  }
  if (title.includes('创意') || title.includes('脚本')) {
    return <BulbOutlined />;
  }
  if (title.includes('账号') || title.includes('设备')) {
    return <TeamOutlined />;
  }
  if (title.includes('数据') || title.includes('监控') || title.includes('算力')) {
    return <BarChartOutlined />;
  }
  return <AppstoreOutlined />;
}

export const MembershipPaymentModal: React.FC<MembershipPaymentModalProps> = ({
  open,
  onClose,
  initialPlanCode,
  mode = 'new',
  currentPlanExpireAt,
  onPaymentSuccess,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansLoaded, setPlansLoaded] = useState(false);
  const [plans, setPlans] = useState<AccountPaymentProduct[]>([]);
  const [entitlement, setEntitlement] = useState<AccountPaymentEntitlement | null>(null);
  const [selectedPlanCode, setSelectedPlanCode] = useState('');
  const [selectedTierCode, setSelectedTierCode] = useState('');
  const [step, setStep] = useState<MembershipPaymentStep>('overview');
  const [paySession, setPaySession] = useState<AccountPaySession | null>(null);
  const [creatingSession, setCreatingSession] = useState(false);

  const tierGroups = useMemo(() => groupPlansByTier(plans), [plans]);
  const selectedTierGroup = useMemo(() => {
    if (!selectedTierCode) {
      return tierGroups[0] || null;
    }
    return tierGroups.find((group) => group.tierCode === selectedTierCode) || tierGroups[0] || null;
  }, [selectedTierCode, tierGroups]);
  const selectedPlan = useMemo(() => {
    if (!selectedPlanCode) {
      return selectedTierGroup?.plans[0] || plans[0] || null;
    }
    return plans.find((plan) => plan.code === selectedPlanCode) || selectedTierGroup?.plans[0] || plans[0] || null;
  }, [plans, selectedPlanCode, selectedTierGroup]);
  const paymentUrl = useMemo(() => buildPaymentUrl(paySession), [paySession]);
  const isRenewMode = mode === 'renew';
  const expireDateText = formatMembershipExpireDate(currentPlanExpireAt);
  const upgradeDetail = useMemo(() => readUpgradeDetail(paySession), [paySession]);
  const paymentActionLabel = useMemo(() => resolvePurchaseTypeLabel(paySession), [paySession]);
  const displayAmountYuan = paySession?.amountYuan || selectedPlan?.amountYuan || '0';
  const displayComputePowerAmount = paySession?.computePowerAmount ?? resolvePlanTotalCompute(selectedPlan);
  const currentPlanLevel = useMemo(() => (
    entitlement?.active ? resolvePlanLevel(entitlement) : 0
  ), [entitlement]);
  const isPlanDisabled = useCallback((plan: AccountPaymentProduct) => {
    const planLevel = resolvePlanLevel(plan);
    return currentPlanLevel > 0 && planLevel > 0 && planLevel < currentPlanLevel;
  }, [currentPlanLevel]);

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    setPlansLoaded(false);
    setPaySession(null);
    setPlans([]);
    try {
      const [payload, entitlementPayload] = await Promise.all([
        listAccountPaymentPlans(),
        getAccountPaymentEntitlement().catch(() => null),
      ]);
      const nextPlans = payload || [];
      setEntitlement(entitlementPayload);
      const currentLevel = entitlementPayload?.active ? resolvePlanLevel(entitlementPayload) : 0;
      setPlans(nextPlans);
      const initialPlan = initialPlanCode
        ? nextPlans.find((plan) => plan.code === initialPlanCode)
        : null;
      const grouped = groupPlansByTier(nextPlans);
      const firstPurchasableGroup = grouped.find((group) => {
        const planLevel = resolvePlanLevel(group.plans[0]);
        return !(currentLevel > 0 && planLevel > 0 && planLevel < currentLevel);
      });
      const firstPurchasablePlan = firstPurchasableGroup
        ? resolveDefaultCheckoutPlan(firstPurchasableGroup.plans)
        : nextPlans.find((plan) => {
            const planLevel = resolvePlanLevel(plan);
            return !(currentLevel > 0 && planLevel > 0 && planLevel < currentLevel);
          });
      const initialLevel = initialPlan ? resolvePlanLevel(initialPlan) : 0;
      const initialDisabled = initialPlan && currentLevel > 0 && initialLevel > 0 && initialLevel < currentLevel;
      const nextSelectedPlan = (initialDisabled ? firstPurchasablePlan : initialPlan || firstPurchasablePlan || nextPlans[0]) || null;
      setSelectedPlanCode(nextSelectedPlan?.code || '');
      setSelectedTierCode(nextSelectedPlan ? resolvePlanTierCode(nextSelectedPlan) : firstPurchasableGroup?.tierCode || grouped[0]?.tierCode || '');
      setStep(initialPlanCode && nextSelectedPlan ? 'checkout' : 'overview');
      logMembershipPaymentDiagnostic('plans-loaded', {
        initialPlanCode: initialPlanCode || '',
        plans: nextPlans.map((plan) => ({
          code: plan.code,
          amountTotal: plan.amountTotal,
          amountYuan: plan.amountYuan,
        })),
      });
    } catch (error) {
      messageApi.error(error instanceof Error ? error.message : '套餐信息加载失败');
      setPlans([]);
      setEntitlement(null);
      setSelectedPlanCode('');
      setSelectedTierCode('');
      setStep('overview');
      logMembershipPaymentDiagnostic('plans-load-failed', {
        message: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setPlansLoading(false);
      setPlansLoaded(true);
    }
  }, [initialPlanCode, messageApi]);

  const createMembershipPaySession = useCallback(async (options?: { refreshExpired?: boolean }) => {
    if (!selectedPlan) {
      return;
    }
    if (isPlanDisabled(selectedPlan)) {
      messageApi.warning('当前已是更高档位，暂不支持购买低档位套餐');
      return;
    }

    setCreatingSession(true);
    logMembershipPaymentDiagnostic('create-session-start', {
      planCode: selectedPlan.code,
      planAmountTotal: selectedPlan.amountTotal,
      planAmountYuan: selectedPlan.amountYuan,
      purchaseType: 'NEW',
    });
    try {
      const payload = await createAccountPaySession({
        planCode: selectedPlan.code,
        productType: 'SUBSCRIPTION',
        purchaseType: 'NEW',
      });
      setPaySession(payload);
      const nextPaymentUrl = buildPaymentUrl(payload);
      const urlInfo = resolvePaymentUrlDebugInfo(nextPaymentUrl);
      logMembershipPaymentDiagnostic('create-session-success', {
        requestedPlanCode: selectedPlan.code,
        requestedPlanAmountTotal: selectedPlan.amountTotal,
        requestedPlanAmountYuan: selectedPlan.amountYuan,
        sessionPlanCode: payload.planCode,
        sessionAmountTotal: payload.amountTotal,
        sessionAmountYuan: payload.amountYuan,
        paymentOrderNo: payload.paymentOrderNo || '',
        paySessionToken: maskPaymentToken(payload.paySessionToken),
        paymentUrlInfo: urlInfo,
      });
      if (options?.refreshExpired) {
        messageApi.info('支付二维码已过期，已为你刷新');
      }
    } catch (error) {
      messageApi.error(error instanceof Error ? error.message : '创建支付会话失败');
      setPaySession(null);
      logMembershipPaymentDiagnostic('create-session-failed', {
        planCode: selectedPlan.code,
        planAmountTotal: selectedPlan.amountTotal,
        planAmountYuan: selectedPlan.amountYuan,
        message: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setCreatingSession(false);
    }
  }, [isPlanDisabled, messageApi, selectedPlan]);

  const handlePaymentPaid = useCallback(async () => {
    await refreshAccountPaymentData();
    await onPaymentSuccess?.();
    messageApi.success('支付成功');
    onClose();
  }, [messageApi, onClose, onPaymentSuccess]);

  usePaymentOrderPolling({
    open,
    paySession,
    onPaid: handlePaymentPaid,
    onError: (error) => {
      console.warn('会员订单状态轮询失败', error);
      messageApi.error(error instanceof Error ? error.message : '支付状态异常，请稍后重试');
    },
  });

  useEffect(() => {
    if (open) {
      void loadPlans();
    } else {
      setPaySession(null);
      setPlans([]);
      setSelectedPlanCode('');
      setSelectedTierCode('');
      setStep('overview');
      setPlansLoaded(false);
      setCreatingSession(false);
    }
  }, [loadPlans, open]);

  useEffect(() => {
    if (step !== 'checkout' || !open || plansLoading || !plansLoaded || !selectedPlan || paySession || creatingSession) {
      return;
    }

    void createMembershipPaySession();
  }, [createMembershipPaySession, creatingSession, open, paySession, plansLoaded, plansLoading, selectedPlan, step]);

  useEffect(() => {
    if (!open || !paySession || !selectedPlan) {
      return;
    }

    if (paySession.planCode !== selectedPlan.code) {
      logMembershipPaymentDiagnostic('session-plan-mismatch-cleared', {
        selectedPlanCode: selectedPlan.code,
        selectedPlanAmountTotal: selectedPlan.amountTotal,
        selectedPlanAmountYuan: selectedPlan.amountYuan,
        sessionPlanCode: paySession.planCode,
        sessionAmountTotal: paySession.amountTotal,
        sessionAmountYuan: paySession.amountYuan,
        paymentOrderNo: paySession.paymentOrderNo || '',
        paySessionToken: maskPaymentToken(paySession.paySessionToken),
      });
      setPaySession(null);
    }
  }, [open, paySession, selectedPlan]);

  useEffect(() => {
    if (!open || !paymentUrl || !paySession || !selectedPlan) {
      return;
    }

    logMembershipPaymentDiagnostic('qr-rendered', {
      selectedPlanCode: selectedPlan.code,
      selectedPlanAmountTotal: selectedPlan.amountTotal,
      selectedPlanAmountYuan: selectedPlan.amountYuan,
      sessionPlanCode: paySession.planCode,
      sessionAmountTotal: paySession.amountTotal,
      sessionAmountYuan: paySession.amountYuan,
      paymentOrderNo: paySession.paymentOrderNo || '',
      paySessionToken: maskPaymentToken(paySession.paySessionToken),
      paymentUrlInfo: resolvePaymentUrlDebugInfo(paymentUrl),
    });
  }, [open, paySession, paymentUrl, selectedPlan]);

  useEffect(() => {
    if (!open || !paySession?.paySessionToken) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setPaySession(null);
      void createMembershipPaySession({ refreshExpired: true });
    }, PAYMENT_QR_EXPIRE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [createMembershipPaySession, open, paySession?.paySessionToken]);

  const handleSelectPlan = (plan: AccountPaymentProduct) => {
    if (isPlanDisabled(plan)) {
      messageApi.warning('当前已是更高档位，暂不支持购买低档位套餐');
      return;
    }
    if (plan.code === selectedPlan?.code) {
      return;
    }
    setSelectedPlanCode(plan.code);
    setPaySession(null);
  };

  const handleEnterCheckout = (group: { tierCode: string; plans: AccountPaymentProduct[] }) => {
    const preferredPlan = resolveDefaultCheckoutPlan(group.plans);
    if (!preferredPlan) {
      return;
    }
    if (isPlanDisabled(preferredPlan)) {
      messageApi.warning('当前已是更高档位，暂不支持购买低档位套餐');
      return;
    }
    setSelectedTierCode(group.tierCode);
    setSelectedPlanCode(preferredPlan.code);
    setPaySession(null);
    setStep('checkout');
  };

  const handleBackToOverview = () => {
    setPaySession(null);
    setStep('overview');
  };

  const renderTierOverview = () => {
    if (plansLoading) {
      return <Skeleton active paragraph={{ rows: 10 }} />;
    }
    if (!tierGroups.length) {
      return (
        <div className="main-layout__membership-empty">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无可购买套餐" />
        </div>
      );
    }
    const tierEntries = tierGroups.map((group) => {
      const representativePlan = group.plans[0];
      return {
        tierCode: group.tierCode,
        plan: representativePlan,
        presentation: resolveTierPresentation(representativePlan),
      };
    });
    const featureMatrixGroups = buildFeatureMatrixGroups(tierEntries);
    return (
      <div className="main-layout__membership-overview">
        <div className="main-layout__membership-overview-head">
          <span>会员套餐</span>
          <h3>选择一个版本，开启爆单模式</h3>
          <p>好的运营，不该浪费在重复工作上。</p>
        </div>
        <div className="main-layout__membership-tier-grid">
          {tierEntries.map(({ tierCode, plan: representativePlan, presentation }) => {
            const tierGroup = tierGroups.find((group) => group.tierCode === tierCode);
            const annualPlan = tierGroup ? resolveTierLowestMonthlyPlan(tierGroup.plans) : null;
            const disabled = representativePlan ? isPlanDisabled(representativePlan) : false;
            return (
              <article
                key={tierCode}
                className={`main-layout__membership-tier-card main-layout__membership-tier-card--${presentation.tone}${disabled ? ' main-layout__membership-tier-card--disabled' : ''}`}
              >
                {presentation.badge ? <span className="main-layout__membership-tier-badge">{presentation.badge}</span> : null}
                <div className="main-layout__membership-tier-top">
                  {presentation.audience ? <span className="main-layout__membership-tier-audience">{presentation.audience}</span> : null}
                  <h4>{presentation.label}</h4>
                  {presentation.summary ? <p>{presentation.summary}</p> : null}
                </div>
                <div className="main-layout__membership-tier-price">
                  <span>最低</span>
                  <strong>¥{annualPlan ? formatAmountTotalYuan(resolvePlanMonthlyAmount(annualPlan)) : '--'}</strong>
                  <em>/月</em>
                </div>
                {presentation.highlights.length ? (
                  <div className="main-layout__membership-tier-highlights">
                    {presentation.highlights.slice(0, 4).map((item) => (
                      <span key={item}>
                        <CheckCircleFilled />
                        {item}
                      </span>
                    ))}
                  </div>
                ) : null}
                <Button
                  type="primary"
                  block
                  disabled={disabled}
                  className="main-layout__membership-tier-buy"
                  onClick={() => tierGroup && handleEnterCheckout(tierGroup)}
                >
                  {disabled ? '当前档位更高' : '立即购买'}
                </Button>
                <div className="main-layout__membership-tier-features">
                  {featureMatrixGroups.map((groupItem) => (
                    <div key={groupItem.title} className="main-layout__membership-tier-feature-group">
                      <b>
                        {renderMembershipFeatureGroupIcon(groupItem.title)}
                        {groupItem.title}
                      </b>
                      <ul>
                        {groupItem.rows.map((row) => {
                          const value = row.values[tierCode] || { supported: false, text: '不支持' };
                          return (
                            <li
                              key={row.key}
                              className={value.supported ? 'main-layout__membership-tier-feature-row--supported' : 'main-layout__membership-tier-feature-row--disabled'}
                            >
                              {value.supported ? <CheckCircleFilled /> : <CloseOutlined />}
                              <span>{row.label}</span>
                              <strong>{value.text}</strong>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDurationSelector = () => {
    const groupPlans = selectedTierGroup?.plans || [];
    return (
      <div className="main-layout__membership-duration-grid" aria-label="选择购买时长">
        {groupPlans.map((plan) => {
          const active = selectedPlan?.code === plan.code;
          const shopLimitText = resolveShopLimitText(plan);
          const disabled = isPlanDisabled(plan);
          const months = resolvePlanDurationSort(plan);
          const monthlyAmount = resolvePlanMonthlyAmount(plan);
          const oneMonthPlan = groupPlans.find((item) => Math.round(resolvePlanDurationSort(item)) === 1);
          const oneMonthTotal = oneMonthPlan ? Number(oneMonthPlan.amountTotal || 0) * Math.max(1, months) : Number(plan.amountTotal || 0);
          const saving = Math.max(0, oneMonthTotal - Number(plan.amountTotal || 0));
          return (
            <Tooltip
              key={plan.code}
              title={disabled ? '当前已是更高档位，暂不支持购买低档位套餐' : ''}
            >
              <span className="main-layout__membership-duration-card-wrap">
                <button
                  type="button"
                  disabled={disabled}
                  className={`main-layout__membership-duration-card${active ? ' main-layout__membership-duration-card--active' : ''}${disabled ? ' main-layout__membership-duration-card--disabled' : ''}`}
                  onClick={() => handleSelectPlan(plan)}
                >
                  {active ? <CheckCircleFilled className="main-layout__membership-duration-check" /> : null}
                  <span className="main-layout__membership-duration-title">{resolvePlanDurationTitle(plan)}</span>
                  <span className="main-layout__membership-duration-price-row">
                    <strong>¥{formatAmountTotalYuan(monthlyAmount)} / 月</strong>
                    {saving > 0 ? <span className="main-layout__membership-duration-discount">省 ¥{formatAmountTotalYuan(saving)}</span> : null}
                  </span>
                  <span className="main-layout__membership-duration-meta">
                    {[shopLimitText, resolvePlanTotalCompute(plan) ? `赠送 ${formatCompute(resolvePlanTotalCompute(plan))} 算力豆` : '']
                      .filter(Boolean)
                      .join(' · ') || '会员权益'}
                  </span>
                </button>
              </span>
            </Tooltip>
          );
        })}
      </div>
    );
  };

  const renderUpgradePopoverContent = () => {
    if (!upgradeDetail) return null;
    const upgradeMonths = Number(upgradeDetail.upgradeMonths || 0);
    const residualDays = Number(upgradeDetail.residualDays || 0);
    const currentTierName = upgradeDetail.currentPlanTierName || upgradeDetail.currentPlanName || '当前档位';
    const targetTierName = upgradeDetail.targetPlanTierName || upgradeDetail.targetPlanName || '目标档位';
    return (
      <div className="main-layout__membership-upgrade-popover">
        <div className="main-layout__membership-upgrade-popover-grid">
          <span>当前剩余</span>
          <strong>{upgradeDetail.remainingDays || 0} 天 {currentTierName}</strong>
          <span>升级抵扣</span>
          <strong>{upgradeMonths > 0 ? `${upgradeMonths} 个月补差价` : '不足 1 个月，不抵扣'}</strong>
          <span>保留原档</span>
          <strong>{residualDays > 0 ? `${residualDays} 天，${targetTierName}到期后继续生效` : '无剩余零头'}</strong>
          <span>算力赠送</span>
          <strong>{formatCompute(upgradeDetail.computePowerAmount)} 算力豆</strong>
        </div>
        <p className="main-layout__membership-upgrade-popover-rule">
          {upgradeDetail.rule || '按当前剩余整月补目标档位差价，剩余不足整月保留原档位'}
        </p>
      </div>
    );
  };

  const renderCheckoutPanel = () => {
    if (plansLoading) {
      return <Skeleton active paragraph={{ rows: 8 }} />;
    }

    if (!selectedPlan) {
      return (
        <div className="main-layout__membership-empty">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无可购买套餐" />
        </div>
      );
    }
    const checkoutPresentation = resolveTierPresentation(selectedPlan);

    return (
      <div className="main-layout__membership-checkout">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          className="main-layout__membership-back"
          onClick={handleBackToOverview}
        >
          返回套餐
        </Button>
        <div className="main-layout__membership-checkout-section">
          <span className="main-layout__membership-checkout-label">购买版本</span>
          <div className={`main-layout__membership-selected-tier main-layout__membership-selected-tier--${checkoutPresentation.tone}`}>
            <div>
              <span>{checkoutPresentation.audience}</span>
              <h3>{checkoutPresentation.label}</h3>
              <p>{checkoutPresentation.summary}</p>
            </div>
            {paymentActionLabel ? <em>{paymentActionLabel}</em> : null}
          </div>
        </div>
        <div className="main-layout__membership-checkout-section">
          <span className="main-layout__membership-checkout-label">购买时长</span>
          {renderDurationSelector()}
        </div>
        <div className="main-layout__membership-checkout-section">
          <span className="main-layout__membership-checkout-label">支付方式</span>
          <div className="main-layout__membership-payment-method main-layout__membership-payment-method--active">
            <CheckCircleFilled className="main-layout__membership-payment-check" />
            <WechatFilled />
            <span>微信支付</span>
          </div>
        </div>
        <div className="main-layout__membership-checkout-total">
          <span>应付金额</span>
          <strong>¥{formatPrice(displayAmountYuan)}</strong>
          {upgradeDetail && paySession?.purchaseType === 'UPGRADE' && (
            <Popover content={renderUpgradePopoverContent()} trigger="hover" placement="bottomLeft">
              <span className="main-layout__membership-checkout-deduction-hint">
                含升级抵扣
                <InfoCircleOutlined />
              </span>
            </Popover>
          )}
          <p>
            {paySession?.creditAmountTotal ? `已抵扣 ¥${paySession.creditAmountYuan}，` : ''}
            {displayComputePowerAmount ? `本次赠送 ${formatCompute(displayComputePowerAmount)} 算力豆` : '支付后自动开通会员权益'}
          </p>
        </div>
      </div>
    );
  };

  const renderPayPanel = () => {
    if (step === 'overview') {
      return null;
    }
    return (
    <div className="main-layout__membership-pay-panel">
      {isRenewMode ? (
        <p className="main-layout__membership-renew-expire">
          当前会员将于 {expireDateText || '--'} 过期
        </p>
      ) : null}
      <div className="main-layout__membership-pay-header">
        <img src={memberScanIcon} alt="" aria-hidden="true" />
        <h3>扫码支付</h3>
      </div>
      <div className="main-layout__membership-qr-card">
        {creatingSession ? (
          <Spin />
        ) : paymentUrl ? (
          <QRCode value={paymentUrl} size={180} bordered={false} />
        ) : (
          <div className="main-layout__membership-qr-placeholder">
            正在生成微信支付二维码
          </div>
        )}
      </div>
      <div className="main-layout__membership-pay-meta">
        <span className="main-layout__membership-pay-provider">
          支持微信扫码
          <img src={memberPayWechatIcon} alt="" aria-hidden="true" />
        </span>
        <p>{paymentActionLabel ? `支付完成后自动${paymentActionLabel}` : '支付完成后自动开通会员'}</p>
        <p className="main-layout__payment-qr-expire-tip">
          二维码有效期 {formatPaymentQrExpireMinutes()} 分钟，过期后自动刷新
        </p>
      </div>
    </div>
    );
  };

  return (
    <>
      {contextHolder}
      <Modal
        open={open}
        centered
        closable={false}
        footer={null}
        width={step === 'overview' ? 'min(1080px, calc(100vw - 72px))' : 'min(1120px, calc(100vw - 72px))'}
        destroyOnHidden
        maskClosable
        onCancel={onClose}
        className="main-layout__membership-modal"
        styles={{ mask: { backgroundColor: 'rgba(0, 0, 0, 0.45)' } }}
      >
        <div className={`main-layout__membership-shell${step === 'overview' ? ' main-layout__membership-shell--overview' : ''}`}>
          <img src={homeEmptyLeft} alt="" className="main-layout__membership-bg main-layout__membership-bg--left" />
          <img src={homeEmptyRight} alt="" className="main-layout__membership-bg main-layout__membership-bg--right" />
          <Button
            type="text"
            className="main-layout__membership-close"
            icon={<CloseOutlined />}
            aria-label="关闭购买会员弹窗"
            onClick={onClose}
          />
          <div className="main-layout__membership-left">
            {step === 'overview' ? renderTierOverview() : renderCheckoutPanel()}
          </div>
          {renderPayPanel()}
        </div>
      </Modal>
    </>
  );
};
